import { useLingui } from "@lingui/react";
import { db, functions, httpsCallable } from "@repo/ui";
import { collection, getDocs, orderBy, query } from "firebase/firestore";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner"; // Assuming sonner is used, typical in shadcn/ui setups. If not available I will adapt.
import { useData } from "../contexts/DataContext";
import type { AppUser } from "../types";
import { Button } from "./ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "./ui/dialog";
import { Label } from "./ui/label";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";

interface SwitchCompanyModalProps {
	user: AppUser;
	isOpen: boolean;
	onClose: () => void;
}

interface CompanyOption {
	id: string;
	name: string;
}

export function SwitchCompanyModal({
	user,
	isOpen,
	onClose,
}: SwitchCompanyModalProps) {
	const { i18n } = useLingui();
	const { refreshUser } = useData();
	const [companies, setCompanies] = useState<CompanyOption[]>([]);
	const [selectedCompanyId, setSelectedCompanyId] = useState<string>(
		user.companyId || "",
	);
	const [isLoading, setIsLoading] = useState(false);
	const [isSwitching, setIsSwitching] = useState(false);

	// Reset selection when modal opens
	useEffect(() => {
		if (isOpen && user.companyId) {
			setSelectedCompanyId(user.companyId);
		}
	}, [isOpen, user.companyId]);

	useEffect(() => {
		if (isOpen) {
			const fetchCompanies = async () => {
				setIsLoading(true);
				try {
					const q = query(collection(db, "companies"), orderBy("name"));
					const querySnapshot = await getDocs(q);
					const fetchedCompanies: CompanyOption[] = [];
					querySnapshot.forEach((doc) => {
						const data = doc.data();
						fetchedCompanies.push({
							id: doc.id,
							name: data.name || i18n._("switchCompany.unnamedCompany"),
						});
					});
					setCompanies(fetchedCompanies);
				} catch (error) {
					console.error("Error fetching companies:", error);
					toast.error(i18n._("switchCompany.loadFailed"));
				} finally {
					setIsLoading(false);
				}
			};

			fetchCompanies();
		}
	}, [isOpen, i18n]);

	const handleSwitch = async () => {
		if (!selectedCompanyId) return;
		if (selectedCompanyId === user.companyId) {
			onClose();
			return;
		}

		setIsSwitching(true);
		try {
			const switchCompanyFn = httpsCallable(functions, "switchCompany");
			await switchCompanyFn({ companyId: selectedCompanyId });

			toast.success(i18n._("switchCompany.switchSuccess"));

			// Refresh user to get new claims and context
			await refreshUser();
			// Note: refreshUser will likely reload the page, so lines after might not run
			onClose();
		} catch (error) {
			console.error("Error switching company:", error);
			// Show error to user
			toast.error(i18n._("switchCompany.switchFailed"));
			setIsSwitching(false);
		}
	};

	return (
		<Dialog open={isOpen} onOpenChange={onClose}>
			<DialogContent className="sm:max-w-[425px]">
				<DialogHeader>
					<DialogTitle>{i18n._("switchCompany.title")}</DialogTitle>
					<DialogDescription>
						{i18n._("switchCompany.description")}
					</DialogDescription>
				</DialogHeader>

				<div className="grid gap-4 py-4 max-h-[300px] overflow-y-auto">
					{isLoading ? (
						<div className="flex justify-center items-center py-8">
							<Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
						</div>
					) : (
						<RadioGroup
							value={selectedCompanyId}
							onValueChange={setSelectedCompanyId}
							className="space-y-2"
						>
							{companies.map((company) => (
								<div
									key={company.id}
									className="flex items-center space-x-2 rounded-md p-2 hover:bg-muted/50 cursor-pointer rtl:space-x-reverse"
								>
									<RadioGroupItem value={company.id} id={company.id} />
									<Label
										htmlFor={company.id}
										className="cursor-pointer flex-grow font-normal"
									>
										{company.name}
										{user.companyId === company.id && (
											<span className="ml-2 text-xs text-muted-foreground rtl:ml-0 rtl:mr-2">
												{i18n._("switchCompany.current")}
											</span>
										)}
									</Label>
								</div>
							))}
						</RadioGroup>
					)}
				</div>

				<DialogFooter>
					<Button variant="outline" onClick={onClose} disabled={isSwitching}>
						{i18n._("common.cancel")}
					</Button>
					<Button
						onClick={handleSwitch}
						disabled={isLoading || isSwitching || !selectedCompanyId}
					>
						{isSwitching && (
							<Loader2 className="mr-2 h-4 w-4 animate-spin rtl:mr-0 rtl:ml-2" />
						)}
						{i18n._("switchCompany.title")}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
